import React, { useMemo } from 'react';
import { CUSTOM_TEXT_MAX_CHARS } from '@chordbaker/music-theory';
import { Theme } from '../contexts/ThemeContext';

interface CustomChordInputProps {
  value: string;
  onChange: (text: string) => void;
  theme: Theme;
}

const createStyles = (theme: Theme) => ({
  container: {
    width: '100%',
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    paddingTop: theme.spacing.sm,
    paddingBottom: theme.spacing.sm,
  },
  input: {
    height: theme.sizes.input.height,
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.radii.md,
    paddingLeft: theme.spacing.md,
    paddingRight: theme.spacing.md,
    fontSize: theme.typography.sizes.md,
    color: theme.colors.text,
    width: '100%',
    boxSizing: 'border-box' as const,
    outline: 'none' as const,
  },
  characterCounter: {
    marginTop: theme.spacing.xs,
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    textAlign: 'right' as const,
  },
  warningText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center' as const,
    lineHeight: theme.typography.lineHeights.normal,
    marginTop: theme.spacing.md,
    marginBottom: 0,
  },
});

const CustomChordInput: React.FC<CustomChordInputProps> = ({ 
  value, 
  onChange, 
  theme 
}) => {
  const styles = useMemo(() => createStyles(theme), [theme]);
  const remainingChars = 20 - value.length;

  return (
    <div style={styles.container}>
      <input
        type="text"
        style={styles.input}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter custom chord text"
        maxLength={CUSTOM_TEXT_MAX_CHARS}
        autoCapitalize="off"
        autoCorrect="off"
        data-testid="custom-chord-input"
      />
      <div style={styles.characterCounter} data-testid="custom-chord-counter">
        {remainingChars} characters left
      </div>
      <div style={styles.warningText} data-testid="custom-chord-warning">
        Custom chords will not display degrees, notes or transpose
      </div>
    </div>
  );
};

export default CustomChordInput;
